// packages/i18n/src/openKeys.ts
//
// The ONE sanctioned bridge from a runtime-built string to a typed translation key.
// Browser-safe: no server imports, no next-intl.
//
// Most dynamic key families are FINITE — `BOOKING_STATUS_TITLE_${status}` where `status`
// is a literal union type-checks against the catalog for free, and a member with no
// catalog entry is a compile error. Those need nothing from this file; write the template
// inline (or with `as const`) and TypeScript does the rest.
//
// The families below are different: their suffix comes from DATABASE ROWS — countries,
// currencies, the gateways a business has configured, the language codes stored on
// passengers, site setups, sales points, passenger-detail option labels. There is no honest
// compile-time union for "whatever rows exist in production", so these go through a
// runtime membership check and return `null` on a miss.
//
// `null` must reach the UI as an error indicator (`<ErrorString>` / `getHtmlErrorString`),
// NEVER as the raw code and never as a plausible default word. A missing country name
// that renders as "GB" looks like a deliberate abbreviation; one that renders as an error
// gets fixed.

import type { CommonKey, CommonT, OpenKeyPrefix } from './keys';
import type { TranslateValues } from '@tourtask/types/i18n';

/**
 * Prefix -> where its suffixes come from. The value is documentation, and the reason each
 * family cannot be a closed union. A missing suffix shows up only at runtime, as the `null`
 * from `translateOpenKeyOrNull` the caller renders as an error indicator.
 */
export const OPEN_KEY_PREFIXES = {
  LANG_TITLE_: 'stored spoken-language codes (offered: ./languageOptions.ts; may predate it, e.g. cmn)',
  COUNTRY_TITLE_: 'ISO 3166-1 alpha-2 country codes',
  CURRENCY_: 'currency codes',
  PAYMENT_OPTION_: 'payment_gateways / config_payment_options / manual-method codes',
  GATEWAY_DESC_: 'payment_gateways.code',
  GATEWAY_DESC_PUBLIC_: 'payment_gateways.code',
  GATEWAY_ABOUT_: 'payment_gateways.code',
  GATEWAY_AUTH_VAR_TITLE_: 'per-adapter auth variable names',
  SITE_SETUP_NAME_: 'site_setups.code',
  SALES_POINT_DEF_TITLE_: 'defSalesPoint.name',
  PAX_DETAIL_OPTION_LABEL_: 'passenger_detail_field_options.labelStringCode',
  PAX_DETAIL_OPTION_LIST_LABEL_: 'passenger_detail_field_options.labelStringCode',
  PAX_DETAIL_FOLLOWUP_LABEL_: 'sparse per PASSENGER_DETAIL_CODES — only numeric follow-ups have one',
  RES_PLAT_DESC_: 'reservation-platform codes',
  DEF_COMP_: 'components.slug — the seeded bank components (guide, vehicle, boat, …)',
  DEF_ITEM_: 'items.slug — the bank tour items, plus their `_DESC` twins',
  MANUAL_METHOD_DESC_: 'manual payment-method codes (cash, check, transfer, …)',
  AGE_GROUP_TITLE_: 'age-group codes — the five standard ones are seeded, businesses add their own',
  PAX_DETAIL_LABEL_: 'passenger_detail_fields.code',
} as const;

/** Just the membership probe, so callers can pass a translator without widening it. */
type HasProbe = { has(key: CommonKey): boolean };

/**
 * Narrow a runtime string to a key, or `null`.
 *
 * The `as CommonKey` inside is the only one in the codebase (ESLint fences it to this
 * file). It is sound because it is GUARDED: `t.has` answers from the dictionary that was
 * actually loaded, so the cast is applied only to a string that is in fact a key.
 */
export function resolveCommonKey(t: HasProbe, key: string): CommonKey | null {
  // `has` is typed to accept only CommonKey; probing with an arbitrary string is exactly
  // what this function exists to do, so the widening stops here.
  return (t.has as (k: string) => boolean)(key) ? (key as CommonKey) : null;
}

/** `resolveCommonKey` for the prefix families, so the prefix itself is type-checked. */
export function resolveOpenKey(t: HasProbe, prefix: OpenKeyPrefix, code: string): CommonKey | null {
  return resolveCommonKey(t, `${prefix}${code}`);
}

/**
 * Translate a key that arrived through an UNTYPED channel.
 *
 * Some keys genuinely cannot be typed at the point of use: a zod `message` that carries a
 * catalog code, an error code from an API response, a `titleCode` column, a label chosen
 * by a registry the caller does not own. Those used to be plain `t(value)` calls, which
 * looked exactly like a checked one and silently accepted anything.
 *
 * This names the seam instead of hiding it. `grep translateUnknownKey` is the list of
 * places where a key is not proven at compile time, and that list should only shrink —
 * each one is a channel that could be typed at its source instead.
 *
 * On a miss the bare key comes back — decided here via `t.has`, not left to the translator,
 * whose own miss value differs by runtime (`common.KEY` on the server, the bare key in the
 * client `I18nProvider`). Prefer
 * {@link translateUnknownKeyOrNull} in new code: `null` forces a visible error indicator,
 * where an echoed code just looks like a broken label.
 */
export function translateUnknownKey(
  t: HasProbe & ((key: CommonKey, values?: TranslateValues) => string),
  key: string,
  values?: TranslateValues,
): string {
  const resolved = resolveCommonKey(t, key);
  return resolved === null ? key : t(resolved, values);
}

/** {@link translateUnknownKey}, reporting a miss as `null` so the caller can show an error. */
export function translateUnknownKeyOrNull(
  t: HasProbe & ((key: CommonKey, values?: TranslateValues) => string),
  key: string,
  values?: TranslateValues,
): string | null {
  const resolved = resolveCommonKey(t, key);
  return resolved === null ? null : t(resolved, values);
}

/**
 * Translate a DB-driven code; the composed KEY comes back when there is no row for it
 * (decided here via `t.has` — a raw server `t()` miss would be `common.<KEY>` instead).
 *
 * This exists so that the ~45 sites that were written as `` t(`PAYMENT_OPTION_${code}`) ``
 * can be typed WITHOUT changing what a user sees. Those sites predate the catalog and
 * several of them detect a miss themselves by comparing the result to the key; silently
 * swapping in a different fallback would break that check as well as the screens.
 *
 * Prefer {@link translateOpenKeyOrNull} in new code. `null` forces the caller to render an
 * error indicator, where an echoed `PAYMENT_OPTION_foo` just looks like a bad label.
 */
export function translateOpenKey(
  t: HasProbe & ((key: CommonKey, values?: TranslateValues) => string),
  prefix: OpenKeyPrefix,
  code: string,
  values?: TranslateValues,
): string {
  const key = resolveOpenKey(t, prefix, code);
  return key === null ? `${prefix}${code}` : t(key, values);
}

/**
 * Translate a DB-driven code, or `null` when no such row exists.
 *
 * Callers MUST handle `null` visibly. Returning the raw code would put
 * `COUNTRY_TITLE_XK` on screen; returning a default word would hide the gap entirely.
 */
export function translateOpenKeyOrNull(
  t: CommonT,
  prefix: OpenKeyPrefix,
  code: string,
  values?: TranslateValues,
): string | null {
  const key = resolveOpenKey(t, prefix, code);
  return key === null ? null : t(key, values);
}

export type { OpenKeyPrefix };
